/**
 * モックノードリポジトリ実装
 * テストや開発用に利用するインメモリのリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import { INodeRepository } from '@/repositories/interfaces';
import { TopicNode } from '@/types/nodeTypes';
import { MockMapRepository } from './MockMapRepository';

/**
 * モックノードリポジトリクラス
 */
export class MockNodeRepository implements INodeRepository {
  /**
   * コンストラクタ
   * @param mapRepository マップリポジトリの参照
   */
  constructor(private mapRepository: MockMapRepository) {}

  /**
   * 指定IDのノードを取得
   */
  public async getNode(mapId: string, nodeId: string): Promise<TopicNode | null> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return null;

    const node = map.nodes.find(n => n.id === nodeId);
    return node ? { ...node } : null;
  }

  /**
   * 指定マップの全ノードを取得
   */
  public async getNodes(mapId: string): Promise<TopicNode[]> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return [];

    return map.nodes.map(node => ({ ...node }));
  }

  /**
   * ノードを更新
   */
  public async updateNode(
    mapId: string,
    nodeId: string,
    update: Partial<TopicNode>
  ): Promise<TopicNode | null> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return null;

    const nodeIndex = map.nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex === -1) return null;

    const updatedNode = {
      ...map.nodes[nodeIndex],
      ...update
    };

    const updatedNodes = [
      ...map.nodes.slice(0, nodeIndex),
      updatedNode,
      ...map.nodes.slice(nodeIndex + 1)
    ];

    await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });
    return { ...updatedNode };
  }

  /**
   * 新しいノードを作成
   */
  public async createNode(mapId: string, node: Omit<TopicNode, 'id'>): Promise<TopicNode> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) throw new Error(`Map with ID ${mapId} not found`);

    const newNode: TopicNode = {
      ...node,
      id: uuidv4()
    };

    const updatedNodes = [...map.nodes, newNode];
    await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });

    return { ...newNode };
  }

  /**
   * ノードを削除
   */
  public async deleteNode(mapId: string, nodeId: string): Promise<boolean> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return false;

    const nodeIndex = map.nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex === -1) return false;

    const updatedNodes = [
      ...map.nodes.slice(0, nodeIndex),
      ...map.nodes.slice(nodeIndex + 1)
    ];

    await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });
    return true;
  }

  /**
   * ノードの位置を更新
   */
  public async updateNodePosition(
    mapId: string,
    nodeId: string,
    position: { x: number; y: number }
  ): Promise<boolean> {
    const node = await this.getNode(mapId, nodeId);
    if (!node) return false;

    await this.updateNode(mapId, nodeId, { position });
    return true;
  }

  /**
   * ノードのステータスを更新
   */
  public async updateNodeStatus(
    mapId: string,
    nodeId: string,
    status: TopicNode['status']
  ): Promise<boolean> {
    const node = await this.getNode(mapId, nodeId);
    if (!node) return false;

    await this.updateNode(mapId, nodeId, { status });
    return true;
  }

  /**
   * 複数のノードを一括で更新
   */
  public async bulkUpdateNodes(
    mapId: string,
    nodesUpdates: { id: string; update: Partial<TopicNode> }[]
  ): Promise<boolean> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return false;

    const updatedNodes = [...map.nodes];
    
    for (const { id, update } of nodesUpdates) {
      const nodeIndex = updatedNodes.findIndex(n => n.id === id);
      if (nodeIndex !== -1) {
        updatedNodes[nodeIndex] = {
          ...updatedNodes[nodeIndex],
          ...update
        };
      }
    }

    await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });
    return true;
  }
}